import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { styled } from '@mui/material/styles';
import { withAuthGuard } from 'src/hocs/with-auth-guard';
import { SideNav } from './side-nav';
import { TopNav } from './top-nav';
import { useVersionCheck } from 'src/hooks/use-version-check';
import { useIdleLogout } from 'src/hooks/use-idle-logout';
import { useAlertaPago } from 'src/hooks/use-alerta-pago';
import { AlertaPagoModal } from 'src/components/alertaPago/AlertaPagoModal';
import { ProveedorSorbyIa, useSorbyIa } from 'src/components/sorbyIa/SorbyIaContext';
import ChatAcopladoSorbyIa from 'src/components/sorbyIa/ChatAcopladoSorbyIa';
import { AvisoBienvenidaModal } from 'src/components/onboardingUsuario/AvisoBienvenidaModal';
import { getEmpresaDetailsFromUser } from 'src/services/empresaService';
import { aterrizaEnSorbyIa } from 'src/utils/sorbyIa/cuentaSorbyIa';
import { separacionFlotante } from 'src/utils/sorbyIa/chatAcoplado';
import onboardingUsuarioService from 'src/services/onboardingUsuarioService';
import { getRemoteVersionFromFirestore } from 'src/services/versionService';
import { useAuthContext } from 'src/contexts/auth-context';

const NAV_WIDTH_EXPANDED = 280;
const NAV_WIDTH_COLLAPSED = 72;

const LayoutRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'navwidth' && prop !== 'reserva' && prop !== 'flotante'
})(({ theme, navwidth, reserva, flotante }) => ({
  display: 'flex',
  flex: '1 1 auto',
  maxWidth: '100%',
  // El chat acoplado empuja el contenido: a la derecha en escritorio, abajo en celular.
  paddingBottom: reserva?.abajo,
  '--sorby-chat-abajo': flotante || '0px',
  // aplicá padding también en md/xl si querés
  [theme.breakpoints.up('lg')]: {
    paddingLeft: Number(navwidth) || NAV_WIDTH_EXPANDED,
    paddingRight: reserva?.derecha || 0
  }
}));


const LayoutContainer = styled('div')({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  width: '100%'
});

// Para una página embebida dentro de otra (la página de obra, `/obra`): acepta las
// mismas props que `Layout` y no dibuja nada alrededor. `Fragment` no sirve: avisa por `title`.
export const SinLayout = ({ children }) => children;

export const Layout = withAuthGuard((props) => (
  <ProveedorSorbyIa>
    <LayoutConChat {...props} />
  </ProveedorSorbyIa>
));

const LayoutConChat = (props) => {
  const { children, title, titleIcon, headerActions } = props;
  const pathname = usePathname();
  const { reserva, visible, disposicion, estado } = useSorbyIa();
  const [openNav, setOpenNav] = useState(false); // mobile
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('sorby_nav_collapsed') === '1';
  });

  const { updateAvailable, triggerReload } = useVersionCheck({
    getRemoteVersion: getRemoteVersionFromFirestore,
    pollMs: 5 * 60 * 1000, // 5 minutos
    localVersion: process.env.NEXT_PUBLIC_APP_VERSION || '',
  });

  // Logout proactivo por inactividad (config por usuario; backend enforcea igual).
  const { user } = useAuthContext();
  useIdleLogout(user?.session_idle_seconds);

  // Del snapshot de empresa que trae el login se toma solo el id: la config del
  // aviso se pide fresca al servidor.
  const alertaPago = useAlertaPago(user?.empresa_id, pathname);

  // Aviso de primera vez (#368). La marca es del usuario y vive en el servidor; acá solo
  // la reflejamos y la apagamos cuando la cierra.
  const [avisoBienvenidaAbierto, setAvisoBienvenidaAbierto] = useState(false);
  const [avisoAltaSola, setAvisoAltaSola] = useState(false);
  // `user` no se actualiza al cerrar: sin esta marca local el aviso reaparecía al cambiar de página (el layout se
  // remonta) o si el servidor tardaba en apagar la marca, y la persona no podía salir.
  const claveAvisoVisto = `aviso_bienvenida_visto:${user?.id ?? user?.user_id ?? user?.email ?? ''}`;
  useEffect(() => {
    if (!user?.aviso_bienvenida_pendiente) return undefined;
    try { if (sessionStorage.getItem(claveAvisoVisto) === '1') return undefined; } catch (_) { /* sin storage: se muestra */ }
    let vigente = true;
    // La variante se decide antes de abrir el aviso para que no cambie el texto a la vista.
    getEmpresaDetailsFromUser(user)
      .then((empresa) => { if (vigente) setAvisoAltaSola(aterrizaEnSorbyIa(empresa)); })
      .catch(() => {})
      .finally(() => { if (vigente) setAvisoBienvenidaAbierto(true); });
    return () => { vigente = false; };
  }, [user?.aviso_bienvenida_pendiente, claveAvisoVisto]);

  const cerrarAvisoBienvenida = useCallback(() => {
    setAvisoBienvenidaAbierto(false);
    try { sessionStorage.setItem(claveAvisoVisto, '1'); } catch (_) { /* sin storage: depende del servidor */ }
    onboardingUsuarioService.marcarAvisoBienvenidaVisto().catch(() => {
      // Si falla, el aviso vuelve en el próximo ingreso: molesta menos que perder el mensaje.
    });
  }, [claveAvisoVisto]);


  const handlePathnameChange = useCallback(() => {
    if (openNav) setOpenNav(false);
  }, [openNav]);

  useEffect(() => {
    handlePathnameChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('sorby_nav_collapsed', collapsed ? '1' : '0');
    }
  }, [collapsed]);

  const navWidth = useMemo(
    () => (collapsed ? NAV_WIDTH_COLLAPSED : NAV_WIDTH_EXPANDED),
    [collapsed]
  );

  return (
    <>
      {/* Opcional: pasá estos props al TopNav si querés un botón de colapso en el header */}
      <TopNav
        onNavOpen={() => setOpenNav(true)}
        onToggleNav={() => setCollapsed((v) => !v)}
        title={title || ""}
        titleIcon={titleIcon}
        collapsed={collapsed}
        navWidth={navWidth}
        updateAvailable={updateAvailable}   // boolean
        onUpdateClick={triggerReload}       // función que hace reload
        headerActions={headerActions}
      />
      <SideNav
        onClose={() => setOpenNav(false)}
        open={openNav}
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((v) => !v)}
        width={navWidth}
      />
      <LayoutRoot navwidth={navWidth} reserva={reserva} flotante={separacionFlotante({ visible, disposicion, estado })}>
        <LayoutContainer>
          {children}
          <ChatAcopladoSorbyIa />
        </LayoutContainer>
      </LayoutRoot>
      <AlertaPagoModal
        open={alertaPago.abierto}
        mensaje={alertaPago.mensaje}
        onClose={alertaPago.cerrar}
      />
      <AvisoBienvenidaModal
        open={avisoBienvenidaAbierto}
        nombre={user?.firstName}
        altaSola={avisoAltaSola}
        onClose={cerrarAvisoBienvenida}
      />
    </>
  );
};
